import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Droplet, ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Quality", to: "/" },
  { label: "Notice", to: "/policies/research-use" },
  { label: "Order", to: "/bacteriostatic-water" },
  { label: "Wholesale", to: "/wholesale" },
  { label: "Shipping", to: "/shipping" },
  { label: "FAQ", to: "/faq" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-slate-200"
          : "bg-transparent border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-7 h-7 bg-slate-950 text-white flex items-center justify-center rounded-sm">
            <Droplet className="w-4 h-4" strokeWidth={2.2} />
          </span>
          <span className="font-chivo font-800 tracking-tight text-lg font-extrabold">
            LAB<span className="text-slate-400">2O</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7" data-testid="nav-links">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="font-plexmono text-[11px] tracking-[0.18em] uppercase text-slate-500 hover:text-slate-950 transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/lab-results"
            className="font-plexmono text-[11px] tracking-[0.18em] uppercase text-slate-500 hover:text-slate-950 transition-colors"
            data-testid="nav-link-lab-results"
          >
            Lab Results
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 border border-slate-950 px-4 py-2 font-plexmono text-[11px] tracking-[0.15em] uppercase hover:bg-slate-950 hover:text-white transition-colors"
            data-testid="nav-cart-button"
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            Cart
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 text-white text-[10px] flex items-center justify-center rounded-full font-inter"
                data-testid="nav-cart-count"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden p-2"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white px-5 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className="text-left font-plexmono text-xs tracking-[0.2em] uppercase text-slate-600"
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/lab-results"
            onClick={() => setMenuOpen(false)}
            className="text-left font-plexmono text-xs tracking-[0.2em] uppercase text-slate-600"
            data-testid="nav-mobile-link-lab-results"
          >
            Lab Results
          </Link>
        </div>
      )}
    </motion.header>
  );
}
